/**
 * Проверяет, является ли устройство мобильным
 */
export const isMobileDevice = () => {
    if (typeof window === "undefined") {
        return false;
    }
    // Проверяем user agent
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileByUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    // Дополнительная проверка: должно быть touch устройство
    const hasTouchSupport = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    // Проверяем размер экрана (мобильные обычно меньше 1024px)
    const isSmallScreen = window.innerWidth <= 1024 && window.screen.width <= 1024;
    return isMobileByUserAgent && hasTouchSupport && isSmallScreen;
};
/**
 * Ключ, под которым Telegram кладёт подписанную init_data в launch-URL
 * мини-аппа: `?tgWebAppData=<urlencoded init_data>` и/или
 * `#tgWebAppData=<urlencoded init_data>`.
 */
const TELEGRAM_INIT_DATA_URL_KEY = "tgWebAppData";
/**
 * Достать сырое значение параметра из строки query/hash БЕЗ преобразования
 * `+` → пробел (именно поэтому не `URLSearchParams`).
 *
 * Подписанная строка уходит на бэкенд байт-в-байт: там по ней считается HMAC
 * (см. verify_init_data), и любая «нормализация» значения сломала бы подпись —
 * получили бы 401 вместо авторизации. `decodeURIComponent` снимает ровно ту
 * обёртку, которую навесил клиент Telegram, и ничего больше.
 */
export function readRawUrlParam(raw, key) {
    if (!raw)
        return null;
    for (const chunk of raw.replace(/^[?#]/, "").split("&")) {
        const eq = chunk.indexOf("=");
        if (eq < 0)
            continue;
        if (chunk.slice(0, eq) !== key)
            continue;
        const value = chunk.slice(eq + 1);
        try {
            return decodeURIComponent(value);
        }
        catch {
            return value;
        }
    }
    return null;
}
/**
 * СТРУКТУРНАЯ проверка, что строка — подписанная Telegram init_data ровно в том
 * виде, как её валидирует бэкенд (verify_init_data: parse_qsl → обязательны
 * пары `hash`/`auth_date`/`user`, `hash` — hex, `user` — JSON с числовым `id`).
 * Возвращает ту же строку, если валидна, иначе null.
 *
 * Именно структурная, а НЕ «в URL есть tgWebAppData»: иначе любой случайный
 * `?tgWebAppData=что-угодно` на обычном сайте переключал бы приложение в режим
 * мини-аппа (Bearer вместо cookie-сессии) и ломал веб-флоу. Полностью
 * симметрично asSignedInitData из max.ts.
 */
export function asSignedTelegramInitData(candidate) {
    if (!candidate)
        return null;
    const params = new URLSearchParams(candidate);
    const hash = params.get("hash");
    const authDate = params.get("auth_date");
    const user = params.get("user");
    if (!hash || !/^[a-f0-9]{16,}$/i.test(hash))
        return null;
    if (!authDate || !/^\d+$/.test(authDate))
        return null;
    if (!user)
        return null;
    try {
        const parsed = JSON.parse(user);
        if (!parsed || typeof parsed.id !== "number")
            return null;
    }
    catch {
        return null;
    }
    return candidate;
}
/** Подписанная TG init_data из одной строки query/hash (или null). */
export function extractTelegramInitData(raw) {
    return asSignedTelegramInitData(readRawUrlParam(raw, TELEGRAM_INIT_DATA_URL_KEY));
}
/**
 * Прочитать подписанную TG init_data из launch-URL (query/hash), НЕ полагаясь
 * на мост. Нужно, потому что telegram-web-app.js подключён с `async` (см.
 * public/index.html) и на мобильной сети может доехать поздно или не доехать
 * вовсе (заблокированный/тормозящий telegram.org) — а подписанная строка
 * физически приходит в адресе страницы. null — если валидных launch-данных
 * Telegram в URL нет (обычный веб).
 */
export function readTelegramInitDataFromLaunchUrl() {
    if (typeof window === "undefined")
        return null;
    return (extractTelegramInitData(window.location.search) ??
        extractTelegramInitData(window.location.hash));
}
/**
 * Снимок TG init_data, сделанный СРАЗУ при загрузке модуля — до первого рендера
 * React, любых редиректов и до того, как мост подчистит launch-параметры из URL
 * (telegram-web-app.js делает history.replaceState при инициализации). Полностью
 * симметрично INITIAL_MAX_INIT_DATA в max.ts.
 *
 * Без снимка получалась гонка: мост ещё не приехал → детект платформы срывался →
 * заголовок X-Telegram-Init-Data не отправлялся → бэк видел гостя, и человека,
 * пришедшего по кнопке бота на `/extend-song?audioId=…`, выкидывало на создание
 * песни. На десктопе клиент инжектит мост сам, поэтому баг ловился только на
 * телефоне и «через раз».
 */
const INITIAL_TELEGRAM_INIT_DATA = (() => {
    if (typeof window === "undefined")
        return null;
    const bridge = window.Telegram?.WebApp?.initData;
    if (typeof bridge === "string" && bridge.trim().length > 0)
        return bridge;
    return readTelegramInitDataFromLaunchUrl();
})();
/**
 * Подписанная launch-init_data Telegram по приоритету источников:
 *  1) живой мост `window.Telegram.WebApp.initData` — самый свежий (клиент мог
 *     перевыпустить строку, например после re-launch мини-аппа);
 *  2) ранний снимок с загрузки модуля (URL мог быть уже затёрт мостом);
 *  3) живое чтение launch-URL (если URL ещё цел).
 * null — не Telegram Mini App.
 */
export const getTelegramLaunchInitData = () => {
    const bridge = getTelegramWebApp()?.initData;
    if (typeof bridge === "string" && bridge.trim().length > 0) {
        return bridge;
    }
    if (INITIAL_TELEGRAM_INIT_DATA) {
        return INITIAL_TELEGRAM_INIT_DATA;
    }
    return readTelegramInitDataFromLaunchUrl();
};
/**
 * Проверяет, запущено ли приложение в Telegram Mini App.
 *
 * Признак — НЕПУСТАЯ подписанная `initData`. Источник — мост
 * `window.Telegram.WebApp.initData` ИЛИ launch-URL/ранний снимок (см.
 * getTelegramLaunchInitData): подписанную строку Telegram кладёт и в адрес
 * страницы запуска, и на мост, только внутри своего клиента. Скрипт
 * telegram-web-app.js подключён глобально в public/index.html, поэтому
 * `window.Telegram.WebApp` есть и на обычном вебе — но там initData пустой и
 * launch-данных в URL нет, так что ложных срабатываний нет.
 *
 * Раньше детект шёл по самому факту наличия `window.Telegram.WebApp` и по
 * хрупким сигналам (UA / hash / referrer) — presence ловил обычный веб как
 * мини-апп, UA/referrer давали ложные срабатывания (внешний браузер, открытый
 * по ссылке t.me). Затем детект переехал на непустой initData МОСТА — и упёрся
 * в то, что мост подключён с `async`: если telegram.org на мобильной сети
 * тормозит или заблокирован (типично для РФ-мобилок), моста нет вовсе, детект
 * возвращал false, и подписанная initData из адреса страницы не доезжала до
 * бэкенда. Отсюда «с компьютера всегда, с телефона через раз»: на десктопе
 * клиент инжектит мост сам. Теперь мост — лишь один из источников, как в max.ts.
 */
export const isTelegramMiniApp = () => {
    if (typeof window === "undefined") {
        return false;
    }
    // Тестовый флаг. localStorage на iOS/WebView бывает null — без try роняем рендер.
    try {
        if (localStorage?.getItem("isTelegramMiniApp") === "true") {
            return true;
        }
    }
    catch {
        /* storage unavailable */
    }
    // Подписанный initData есть только внутри клиента Telegram (мост / launch-URL).
    return getTelegramLaunchInitData() != null;
};
/**
 * Загружает скрипт Telegram WebApp, если его нет
 */
export const loadTelegramWebAppScript = () => {
    return new Promise((resolve, reject) => {
        if (typeof window === "undefined") {
            reject(new Error("Window is not available"));
            return;
        }
        // Если скрипт уже загружен
        if (window.Telegram?.WebApp) {
            resolve();
            return;
        }
        // Если скрипт уже загружается
        const existingScript = document.querySelector('script[src*="telegram.org"]');
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve());
            existingScript.addEventListener("error", () => reject(new Error("Failed to load Telegram WebApp script")));
            return;
        }
        // Загружаем скрипт
        const script = document.createElement("script");
        script.src = "https://telegram.org/js/telegram-web-app.js";
        script.async = true;
        script.onload = () => {
            // Ждем, пока WebApp инициализируется
            const checkInterval = setInterval(() => {
                if (window.Telegram?.WebApp) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 50);
            // Таймаут на случай, если скрипт загрузился, но WebApp не инициализировался
            setTimeout(() => {
                clearInterval(checkInterval);
                if (window.Telegram?.WebApp) {
                    resolve();
                }
                else {
                    reject(new Error("Telegram WebApp script loaded but WebApp is not available"));
                }
            }, 2000);
        };
        script.onerror = () => reject(new Error("Failed to load Telegram WebApp script"));
        document.head.appendChild(script);
    });
};
/**
 * Получает объект Telegram WebApp API (если доступен)
 */
export const getTelegramWebApp = () => {
    if (typeof window !== "undefined" && window.Telegram?.WebApp) {
        return window.Telegram.WebApp;
    }
    return null;
};
/**
 * Получает initData для тестирования из URL параметров или localStorage
 * Для тестирования можно передать initData через URL параметр ?initData=...
 * или установить localStorage.setItem("testInitData", "...")
 */
export const getTestInitData = () => {
    if (typeof window === "undefined") {
        return null;
    }
    // Проверяем URL параметры
    const searchParams = new URLSearchParams(window.location.search);
    const urlInitData = searchParams.get("initData");
    if (urlInitData) {
        return urlInitData;
    }
    try {
        return localStorage?.getItem("testInitData") ?? null;
    }
    catch {
        return null;
    }
};
/**
 * Получает initData из Telegram WebApp или тестовых источников.
 *
 * Сначала — подписанная launch-строка (мост → ранний снимок → URL, см.
 * getTelegramLaunchInitData). Она и уходит в заголовок X-Telegram-Init-Data,
 * поэтому НЕ зависит от того, доехал ли мост: раньше здесь URL-фолбэк был
 * недостижим, потому что вызывающий код гейтил его детектом по мосту.
 * Ниже — нестрогие источники для ручной отладки в браузере (сырой
 * `tgWebAppData` без подписи, `?initData=`, localStorage): они специально не
 * участвуют в детекте платформы, чтобы случайный параметр в адресе обычного
 * сайта не переключал приложение в режим мини-аппа.
 */
export const getInitData = () => {
    const launchInitData = getTelegramLaunchInitData();
    if (launchInitData) {
        return launchInitData;
    }
    const webApp = getTelegramWebApp();
    // Альтернативный способ: проверяем initDataUnsafe (может быть доступен раньше)
    if (webApp?.initDataUnsafe && Object.keys(webApp.initDataUnsafe).length > 0) {
        // Если initDataUnsafe доступен, но initData нет, это может означать,
        // что нужно подождать или использовать другой способ получения
        console.log("initDataUnsafe available but initData is not:", webApp.initDataUnsafe);
    }
    // Отладочный/нестрогий фолбэк: сырой tgWebAppData в query или hash — без
    // структурной проверки подписи (её уже сделал getTelegramLaunchInitData).
    const rawFromUrl = readRawUrlParam(window.location.search, TELEGRAM_INIT_DATA_URL_KEY) ??
        readRawUrlParam(window.location.hash, TELEGRAM_INIT_DATA_URL_KEY);
    if (rawFromUrl) {
        return rawFromUrl;
    }
    // Для тестирования в браузере
    const testData = getTestInitData();
    if (testData) {
        return testData;
    }
    return null;
};
/**
 * Ждет, пока Telegram WebApp инициализируется и initData станет доступен
 */
export const waitForInitData = async (maxWaitTime = 2000, checkInterval = 100) => {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        const initData = getInitData();
        if (initData) {
            return initData;
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
    return null;
};
